import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import React, { Suspense, lazy, useEffect } from "react";
import { Beforeunload } from "react-beforeunload";
import "react-clock/dist/Clock.css";
import "react-modern-drawer/dist/index.css";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import { io } from "socket.io-client";
import BeforeLoginRoute from "./Helper/BeforeLoginRoute";
import ProtectedRouter from "./Helper/ProtectedRoute";
import "./assets/Styles/style.css";
import "./assets/Styles/table.css";
import { Get } from "./axios/axios-functions";
import { Loader } from "./components/Loader";
import { BaseURL, apiUrl } from "./config/apiUrl";
import { updateUser } from "./store/auth/authSlice";
import {
  saveChatCounter,
  saveNotiData,
  setAllDepartments,
} from "./store/common/commonSlice";

const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndCondition = lazy(() => import("./pages/TermsAndCondition"));
const ContactUs = lazy(() => import("./pages/ContactUs/ContactUs"));
const Cms = lazy(() => import("./pages/Cms/Cms"));
const CmsDetails = lazy(() => import("./pages/CmsDetails/CmsDetails"));

const ForgetPassword = lazy(() =>
  import("./pages/ForgetPassword/ForgotPassword")
);
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword/ResetPassword"));
const VerifyOTP = lazy(() => import("./pages/VerifyOTP/VerifyOTP"));
const Login = lazy(() => import("./pages/Login/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const Jobs = lazy(() => import("./pages/Jobs/Jobs"));
const AddOrder = lazy(() => import("./pages/AddOrder/AddOrder"));
const Drivers = lazy(() => import("./pages/Accounts/Drivers/Drivers"));
const DriverDetails = lazy(() =>
  import("./pages/Accounts/DriverDetail/DriverDetail")
);
const AddDriver = lazy(() => import("./pages/Accounts/AddDriver/AddDriver"));
const Users = lazy(() => import("./pages/Accounts/Users/Users"));
const UserDetail = lazy(() => import("./pages/Accounts/UserDetail/UserDetail"));
const Corporates = lazy(() => import("./pages/Accounts/Corporates/Corporates"));
const AddCorporate = lazy(() =>
  import("./pages/Accounts/AddCorporate/AddCorporate")
);
const CorporateDetail = lazy(() =>
  import("./pages/Accounts/CorporateDetail/CorporateDetail")
);
const UnpaidInvoice = lazy(() =>
  import("./pages/Accounts/UnpaidInvoice/UnpaidInvoice")
);
const AccountRequest = lazy(() =>
  import("./pages/AccountRequest/AccountRequest")
);
const AccountRequestDetails = lazy(() =>
  import("./pages/AccountRequestDetails/AccountRequestDetails")
);
const Inventory = lazy(() => import("./pages/Inventory/Inventory"));
const LeaveRequest = lazy(() => import("./pages/LeaveRequest/LeaveRequest"));
const Overview = lazy(() => import("./pages/Finance/Overview/Overview"));
const Invoice = lazy(() => import("./pages/Finance/Invoice/Invoice"));
const Bill = lazy(() => import("./pages/Finance/Bill/Bill"));
const BillDetails = lazy(() =>
  import("./pages/Finance/BillDetails/BillDetails")
);
const Settings = lazy(() => import("./pages/Settings/Settings"));
const Chat = lazy(() => import("./pages/Chat/Chat"));
const Claims = lazy(() => import("./pages/Claims/Claims"));
const Notification = lazy(() => import("./pages/Notification/Notification"));
const ChartOfAccount = lazy(() =>
  import("./pages/Finance/ChartOfAccount/ChartOfAccount")
);

export let socket = null;

function App() {
  const { user, isLogin, accessToken } = useSelector(
    (state) => state?.authReducer
  );
  const userDataState = useSelector((state) => state?.authReducer?.user);
  const dispatch = useDispatch();

  const fetchNotificationsForCount = async () => {
    const url = BaseURL("notifications/all?page=1&limit=5&noCountUpdate=true");
    let response = await Get(url, accessToken);

    if (response !== undefined) {
      response?.data?.newNotifications > 0 &&
        dispatch(saveNotiData(response?.data?.newNotifications));
    }
  };

  const getDepartments = async () => {
    const url = BaseURL("department/all");
    const response = await Get(url, accessToken);
    if (response !== undefined) {
      dispatch(setAllDepartments(response?.data?.data));
    }
  };

  const getMe = async () => {
    const url = BaseURL("users/me");
    const response = await Get(url, accessToken);

    if (response !== undefined) {
      const apiUser = response?.data?.data?.user;
      dispatch(updateUser(apiUser));
    }
  };

  useEffect(() => {
    if (isLogin) {
      socket = io(apiUrl, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionDelay: 180000,
        reconnectionDelayMax: 300000,
      });
      socket?.emit("join", userDataState?._id);
      // fetchNotificationsForCount();
      // getDepartments();
      getMe();
    }
  }, [isLogin]);

  useEffect(() => {
    socket?.on("new-notification", (notification, number) => {
      //something went wrong here
      if (
        notification?.receiver == userDataState?._id &&
        window.location.pathname !== "/notifications" &&
        notification?.flag == "project"
      ) {
        dispatch(saveNotiData());
      }
      //something went wrong here

      if (
        notification?.receiver == userDataState?._id &&
        notification?.flag == "chat" &&
        window.location.pathname !== "/chat"
      ) {
        dispatch(saveChatCounter(notification));
        dispatch(saveNotiData());
      }
    });
    return () => {
      socket?.off("new-notification");
    };
  }, []);

  return (
    <>
      <ToastContainer />
      <Beforeunload
        onBeforeunload={() => {
          if (isLogin) {
            socket?.emit("disconnected", user?._id);
          }
        }}
      />
      <BrowserRouter>
        <Suspense fallback={<Loader className={"mvh-100"} />}>
          <Routes>
            <Route
              path="/privacy-policy"
              exact
              element={<BeforeLoginRoute element={<PrivacyPolicy />} />}
            />
            <Route
              path="/terms-conditions"
              exact
              element={<BeforeLoginRoute element={<TermsAndCondition />} />}
            />
            <Route
              path="/contact-us"
              exact
              element={<BeforeLoginRoute element={<ContactUs />} />}
            />
            <Route
              path="/"
              exact
              element={<BeforeLoginRoute element={<Login />} />}
            />
            <Route
              path="/forgot-password"
              exact
              element={<BeforeLoginRoute element={<ForgetPassword />} />}
            />
            <Route
              path="/verify-otp"
              exact
              element={<BeforeLoginRoute element={<VerifyOTP />} />}
            />
            <Route
              path="/reset-password"
              exact
              element={<BeforeLoginRoute element={<ResetPassword />} />}
            />
            <Route
              path="/dashboard"
              exact
              element={<ProtectedRouter element={<Dashboard />} />}
            />
            <Route
              path="/jobs"
              exact
              element={<ProtectedRouter element={<Jobs />} />}
            />
            <Route
              path="/jobs/add-order"
              exact
              element={<ProtectedRouter element={<AddOrder />} />}
            />
            {/* Accounts */}
            <Route
              path="/account/drivers"
              exact
              element={<ProtectedRouter element={<Drivers />} />}
            />
            <Route
              path="/account/drivers/:slug"
              exact
              element={<ProtectedRouter element={<DriverDetails />} />}
            />
            <Route
              path="/account/add-driver"
              exact
              element={<ProtectedRouter element={<AddDriver />} />}
            />
            <Route
              path="/account/users"
              exact
              element={<ProtectedRouter element={<Users />} />}
            />
            <Route
              path="/account/users/:slug"
              exact
              element={<ProtectedRouter element={<UserDetail />} />}
            />
            <Route
              path="/account/corporates"
              exact
              element={<ProtectedRouter element={<Corporates />} />}
            />

            <Route
              path="/account/add-corporate"
              exact
              element={<ProtectedRouter element={<AddCorporate />} />}
            />

            <Route
              path="/account/corporates/:slug"
              exact
              element={<ProtectedRouter element={<CorporateDetail />} />}
            />

            <Route
              path="/account/unpaid-invoice"
              exact
              element={<ProtectedRouter element={<UnpaidInvoice />} />}
            />

            {/* Inventory */}
            <Route
              path="/inventory"
              exact
              element={<ProtectedRouter element={<Inventory />} />}
            />

            {/* Account Request */}
            <Route
              path="/account-request"
              exact
              element={<ProtectedRouter element={<AccountRequest />} />}
            />
            <Route
              path="/account-request/:slug"
              exact
              element={<ProtectedRouter element={<AccountRequestDetails />} />}
            />
            {/* Leave Request */}
            <Route
              path="/leave-request"
              exact
              element={<ProtectedRouter element={<LeaveRequest />} />}
            />
            {/* Finance */}
            <Route
              path="/leave-request"
              exact
              element={<ProtectedRouter element={<LeaveRequest />} />}
            />
            <Route
              path="/finance/invoice"
              exact
              element={<ProtectedRouter element={<Invoice />} />}
            />
            <Route
              path="/finance/overview"
              exact
              element={<ProtectedRouter element={<Overview />} />}
            />
            <Route
              path="/finance/bills"
              exact
              element={<ProtectedRouter element={<Bill />} />}
            />
            <Route
              path="/finance/bills/:slug"
              exact
              element={<ProtectedRouter element={<BillDetails />} />}
            />

            <Route
              path="/finance/chart-of-account"
              exact
              element={<ProtectedRouter element={<ChartOfAccount />} />}
            />

            {/* Chat */}
            <Route
              path="/chats"
              exact
              element={<ProtectedRouter element={<Chat />} />}
            />
            <Route
              path="/claims"
              exact
              element={<ProtectedRouter element={<Claims />} />}
            />

            {/* Settings */}
            <Route
              path="/settings"
              exact
              element={<ProtectedRouter element={<Settings />} />}
            />

            <Route
              path="/cms"
              exact
              element={<ProtectedRouter element={<Cms />} />}
            />

            <Route
              path="/cms/:slug"
              exact
              element={<ProtectedRouter element={<CmsDetails />} />}
            />

            <Route
              path="/notifications"
              exact
              element={<ProtectedRouter element={<Notification />} />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
